import { hexToRgb } from "../core/color";
import type { Body } from "./world";

/**
 * Puentes dirigidos entre cuerpos.
 *
 * En vez de inflar el radio de fusion global (lo que deforma todo el campo y
 * crea "almendras" flotantes entre cuerpos que no deberian conectarse), se
 * precalcula una lista de enlaces concretos: cada cuerpo tiende un puente SOLO
 * hacia los otros cuerpos que caen dentro de su radio de alcance, a lo largo de
 * la recta que une sus centros. Asi el puente es dirigido (nunca sale "hacia
 * todos lados") y el coste por pixel escala con el numero real de enlaces, no
 * con el cuadrado de los cuerpos.
 */
export interface BridgeLink {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  /** Grosor del cuello en el centro del puente, en unidades de mundo. */
  r: number;
  /** Radio de fusion suave del enlace con el resto del campo. */
  k: number;
  /** Desfase propio del enlace (para que cada puente ondule distinto). */
  phase: number;
  /** Color mezclado de los dos cuerpos. */
  r8: number;
  g8: number;
  b8: number;
  /** Opacidad del puente por distancia radial a cada cuerpo: llena dentro del
   *  cuerpo (inner), se desvanece a 0 en el borde de su area de alcance (outer).
   *  Asi el difuminado llega EXACTAMENTE hasta el circulo de alcance dibujado. */
  innerA: number;
  outerA: number;
  innerB: number;
  outerB: number;
  /** Caja envolvente (ya expandida) para descarte rapido por pixel en CPU. */
  minx: number;
  miny: number;
  maxx: number;
  maxy: number;
}

/** Alcance efectivo de un cuerpo: propio si >= 0, si no el global. */
export function bodyReach(body: Body, globalReach: number): number {
  return body.bridgeReach >= 0 ? body.bridgeReach : globalReach;
}

/**
 * Calcula los puentes activos. O(n log n) con hash espacial cuando estan
 * dispersos; fallback a O(n^2) cuando estan agrupados. El resultado se
 * reutiliza en todos los pixeles.
 */
export function computeBridges(bodies: readonly Body[], globalReach: number, fade = false): BridgeLink[] {
  const links: BridgeLink[] = [];
  const n = bodies.length;
  if (n < 2) return links;

  // Celda = max(alcance*2, 280) para que alcance grande cubra vecinos lejanos.
  let maxR = globalReach;
  for (let i = 0; i < n; i++) {
    const br = bodies[i].bridgeReach;
    if (br >= 0 && br > maxR) maxR = br;
  }
  const cell = Math.max(maxR * 2 + 60, 280);
  const invCell = 1 / cell;

  const grid = new Map<string, number[]>();
  const keyFor = (x: number, y: number): string =>
    `${Math.floor(x * invCell)},${Math.floor(y * invCell)}`;

  for (let i = 0; i < n; i++) {
    const b = bodies[i];
    if (bodyReach(b, globalReach) <= 0) continue;
    const k = keyFor(b.pos.x, b.pos.y);
    const arr = grid.get(k);
    if (arr) arr.push(i);
    else grid.set(k, [i]);
  }

  let maxCellCount = 0;
  for (const arr of grid.values()) if (arr.length > maxCellCount) maxCellCount = arr.length;
  let activeCount = 0;
  for (let i = 0; i < n; i++) if (bodyReach(bodies[i], globalReach) > 0) activeCount++;
  const useGrid = activeCount === 0 || maxCellCount / activeCount < 0.6;

  const tryPair = (i: number, j: number): void => {
    const a = bodies[i];
    const b = bodies[j];
    const ra = bodyReach(a, globalReach);
    const rb = bodyReach(b, globalReach);
    if (ra <= 0 || rb <= 0) return;
    if (a.group !== 0 && b.group !== 0 && a.group !== b.group) return;
    const dx = b.pos.x - a.pos.x;
    const dy = b.pos.y - a.pos.y;
    const dist = Math.hypot(dx, dy);
    const gap = dist - a.radius - b.radius;
    const reachSum = ra + rb;
    const limit = fade ? reachSum * 1.5 : reachSum;
    if (gap <= 0 || gap >= limit) return;
    const cover = Math.max(0, Math.min(1, 1 - gap / reachSum));
    const neck = Math.min(a.radius, b.radius) * 0.6 * (0.4 + 0.6 * cover);
    if (neck < 3) return;
    const ca = hexToRgb(a.color);
    const cb = hexToRgb(b.color);
    const k = Math.max(4, neck);
    const pad = neck * 4 + k + 4;
    links.push({
      ax: a.pos.x,
      ay: a.pos.y,
      bx: b.pos.x,
      by: b.pos.y,
      r: neck,
      k,
      phase: ((i * 73856093) ^ (j * 19349663)) % 628 / 100,
      r8: Math.round((ca.r + cb.r) * 0.5),
      g8: Math.round((ca.g + cb.g) * 0.5),
      b8: Math.round((ca.b + cb.b) * 0.5),
      innerA: a.radius,
      outerA: a.radius + ra,
      innerB: b.radius,
      outerB: b.radius + rb,
      minx: Math.min(a.pos.x, b.pos.x) - pad,
      miny: Math.min(a.pos.y, b.pos.y) - pad,
      maxx: Math.max(a.pos.x, b.pos.x) + pad,
      maxy: Math.max(a.pos.y, b.pos.y) + pad,
    });
  };

  if (!useGrid) {
    for (let i = 0; i < n; i++) {
      if (bodyReach(bodies[i], globalReach) <= 0) continue;
      for (let j = i + 1; j < n; j++) tryPair(i, j);
    }
    return links;
  }

  const visited = new Set<string>();
  for (const [key, arr] of grid) {
    const [cxStr, cyStr] = key.split(",");
    const cx = parseInt(cxStr, 10);
    const cy = parseInt(cyStr, 10);
    for (let a = 0; a < arr.length; a++) {
      for (let b = a + 1; b < arr.length; b++) tryPair(arr[a], arr[b]);
    }
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        if (dx === 0 && dy === 0) continue;
        if (dx < 0) continue;
        if (dx === 0 && dy < 0) continue;
        const nk = `${cx + dx},${cy + dy}`;
        const pairKey = `${key}|${nk}`;
        if (visited.has(pairKey)) continue;
        const other = grid.get(nk);
        if (!other) continue;
        visited.add(pairKey);
        visited.add(`${nk}|${key}`);
        for (const ia of arr) for (const ib of other) {
          if (ia < ib) tryPair(ia, ib);
          else tryPair(ib, ia);
        }
      }
    }
  }
  return links;
}

/** Parte fraccionaria (espejo de fract() de GLSL). */
const fract = (x: number): number => x - Math.floor(x);

/** smoothstep de GLSL. */
const smoothstep01 = (e0: number, e1: number, x: number): number => {
  if (e0 === e1) return x < e0 ? 0 : 1;
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

/** Distancia con signo a un segmento (para el cuello capsular del puente). */
export function sdSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const pax = px - ax;
  const pay = py - ay;
  const bax = bx - ax;
  const bay = by - ay;
  const denom = bax * bax + bay * bay;
  const h = denom < 1e-9 ? 0 : Math.min(1, Math.max(0, (pax * bax + pay * bay) / denom));
  const dx = pax - bax * h;
  const dy = pay - bay * h;
  return Math.hypot(dx, dy);
}

/** Opciones de render de los puentes (espejo de los uniforms del shader). */
export interface BridgeOpts {
  style: number;
  threads: number;
  threadReach: number;
  flare: number;
  time: number;
}

export const DEFAULT_BRIDGE_OPTS: BridgeOpts = { style: 0, threads: 1, threadReach: 0.5, flare: 0, time: 0 };

/**
 * Distancia con signo de un puente segun su estilo y conectores (espejo CPU
 * del shader). style: 0 recto, 1 desgarrado (agujeros), 2 organico (ondula).
 * `threads` divide el puente en hilos; `flare` lo ensancha en los extremos.
 */
export function linkField(
  px: number,
  py: number,
  link: BridgeLink,
  opts: BridgeOpts,
): number {
  const { style, time } = opts;
  const bax = link.bx - link.ax;
  const bay = link.by - link.ay;
  const len = Math.max(1e-4, Math.hypot(bax, bay));
  const dirx = bax / len;
  const diry = bay / len;
  const pax = px - link.ax;
  const pay = py - link.ay;
  const along = pax * dirx + pay * diry;
  const h = Math.min(1, Math.max(0, along / len));
  const perp = pax * -diry + pay * dirx;

  const ends = Math.pow(Math.abs(2 * h - 1), 2);
  const rLocal = link.r * (1 + opts.flare * 1.6 * ends);
  const env = 1 - ends;
  const freq = 6.2831 * (len / 220 + 0.5);

  // Cuello principal unico (ondulacion organica en el centro).
  const neckOff = style === 2 ? link.r * 1.1 * env * Math.sin(h * freq + time * 1.6 + link.phase) : 0;
  const neckD = along < 0 ? Math.hypot(pax, pay) : along > len ? Math.hypot(px - link.bx, py - link.by) : Math.abs(perp - neckOff);
  let best = neckD - rLocal;

  // Hilos = CORTES tallados en el cuello cerca de la conexion (resta).
  const n = opts.threads < 1 ? 1 : opts.threads;
  if (n > 1) {
    const e = Math.abs(2 * h - 1);
    const span = Math.min(1, Math.max(0.05, opts.threadReach));
    const band = 0.15;
    const hi = Math.min(1, 1 - span + band);
    const lo = hi - band;
    const cutProfile = smoothstep01(lo, hi, e);
    const cutW = rLocal * 0.14;
    const spread = rLocal * 1.6;
    for (let i = 0; i < n - 1; i++) {
      const o = spread * ((i + 1) / n - 0.5) + (style === 2 ? link.r * 0.5 * Math.sin(h * freq + time * 1.6 + link.phase + i) : 0);
      const groove = Math.abs(perp - neckOff - o) - cutW;
      const carved = Math.max(best, -groove);
      best = best + (carved - best) * cutProfile;
    }
  }

  if (style === 1) {
    const cell = Math.max(6, link.r * 2.2);
    const idx = Math.floor(along / cell);
    const localc = along - (idx + 0.5) * cell;
    const rnd = fract(Math.sin(idx * 12.9898 + link.phase * 7) * 43758.5453);
    const rnd2 = fract(Math.sin(idx * 78.233 + link.phase * 3) * 24634.6345);
    const holeR = link.r * (0.3 + 0.45 * rnd);
    const perpOff = (rnd2 - 0.5) * link.r * 0.9;
    const dh = Math.hypot(localc, perp - perpOff) - holeR;
    best = Math.max(best, -dh);
  }
  return best;
}
